import { useMutation } from "@tanstack/react-query";
import {
  ApplicationFeature,
  EntityType,
  UploadedFileFeature,
  UserProfileDto,
  UserRole,
} from "@green0meter/api-client";

import { DOMAIN_FILE_STORAGE } from "../domain";

import { fileStorageApi } from "@api/services";
import { roleGuard } from "@api";

export const ACTION_UPLOAD_FILE = "UPLOAD_FILE";

const allowedRoles = [UserRole.Owner, UserRole.Manager, UserRole.Respondent];

export type UploadedFileInfoModel = {
  fileType: string;
  fileSize: number;
  originalName: string;
  path: string;
  url: string;
  fileId: string;
};

type UploadFileProps = {
  file: File;
  entityId: string;
  databaseEntityType: EntityType;
  uploadedFileFeature: UploadedFileFeature;
  relatedFeature?: ApplicationFeature;
};

const uploadFile = async (
  props: UploadFileProps,
): Promise<UploadedFileInfoModel> => {
  const response = await fileStorageApi.abstractFileStorageControllerUploadFile(
    props.file,
    props.entityId,
    props.databaseEntityType,
    props.uploadedFileFeature,
    props.relatedFeature || null,
  );

  return response.data;
};

export const useUploadFile = (
  user: UserProfileDto | null,
): {
  uploadFile: (props: UploadFileProps) => void;
  uploadFileAsync: (props: UploadFileProps) => Promise<UploadedFileInfoModel>;
  uploadFileEnabled: boolean;
  isSuccessUploadFile: boolean;
  isPendingUploadFile: boolean;
  uploadFileReset: () => void;
} => {
  const enabled = roleGuard(user, allowedRoles);
  const { mutate, mutateAsync, isSuccess, isPending, reset } = useMutation<
    UploadedFileInfoModel,
    Error,
    UploadFileProps
  >({
    mutationKey: [DOMAIN_FILE_STORAGE, ACTION_UPLOAD_FILE],
    mutationFn: enabled
      ? uploadFile
      : async (): Promise<UploadedFileInfoModel> => {
          throw new Error(
            "Insufficient permissions to upload files. Required roles: Owner, Manager, or Respondent.",
          );
        },
  });

  return {
    uploadFile: mutate,
    uploadFileAsync: mutateAsync,
    uploadFileEnabled: enabled,
    isSuccessUploadFile: isSuccess,
    isPendingUploadFile: isPending,
    uploadFileReset: reset,
  };
};
